import React, { useState, useEffect } from 'react';
import { Info, Search, Edit, Save, X, CheckCircle, AlertCircle, Filter } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '../utils/supabase/info';

interface Question {
  id: string;
  category: string;
  questionText: string;
  questionType: string;
  placeholder?: string;
  helpText?: string;
  options?: string[];
  source: string;
  hasOverride?: boolean;
  lastModified?: string;
  modifiedBy?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  assets: 'Asset Management',
  incidents: 'Incident Management',
  service_requests: 'Service Requests',
  problems: 'Problem Management',
  changes: 'Change Management',
};

const QUESTION_TYPE_LABELS: Record<string, string> = {
  boolean: 'Yes/No',
  multiple_choice: 'Multiple Choice',
  checkbox: 'Checkboxes',
  textarea: 'Long Text',
  text: 'Short Text',
  select: 'Dropdown',
};

interface QuestionLibraryProps {
  accessToken?: string;
}

export function QuestionLibrary({ accessToken }: QuestionLibraryProps) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [editedHelpText, setEditedHelpText] = useState('');
  const [saving, setSaving] = useState(false);
  const [filterMissingHelpText, setFilterMissingHelpText] = useState(false);

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const token = accessToken || localStorage.getItem('session_token') || localStorage.getItem('pathkit_session_id') || localStorage.getItem('pathkit_access_token');
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/question-library/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load questions');
      }

      const data = await response.json();
      setQuestions(data);
    } catch (error) {
      console.error('[QUESTION_LIBRARY] Load error:', error);
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (question: Question) => {
    setEditingQuestion(question);
    setEditedHelpText(question.helpText || '');
  };

  const handleSave = async () => {
    if (!editingQuestion) return;

    try {
      setSaving(true);
      const token = accessToken || localStorage.getItem('session_token') || localStorage.getItem('pathkit_session_id') || localStorage.getItem('pathkit_access_token');

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/question-library/${editingQuestion.id}/helptext`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ helpText: editedHelpText }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save helpText');
      }

      toast.success('HelpText saved successfully');
      setEditingQuestion(null);
      loadQuestions();
    } catch (error) {
      console.error('[QUESTION_LIBRARY] Save error:', error);
      toast.error('Failed to save helpText');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async (questionId: string) => {
    try {
      const token = accessToken || localStorage.getItem('session_token') || localStorage.getItem('pathkit_session_id') || localStorage.getItem('pathkit_access_token');

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/question-library/${questionId}/helptext`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to revert helpText');
      }

      toast.success('Reverted to default helpText');
      loadQuestions();
    } catch (error) {
      console.error('[QUESTION_LIBRARY] Revert error:', error);
      toast.error('Failed to revert helpText');
    }
  };

  // Filter questions
  const filteredQuestions = questions.filter((q) => {
    const matchesSearch = 
      q.questionText.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.id.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = 
      selectedCategory === 'all' || q.category === selectedCategory;
    
    const matchesMissingFilter = 
      !filterMissingHelpText || !q.helpText;

    return matchesSearch && matchesCategory && matchesMissingFilter;
  });

  // Group by category for display
  const groupedQuestions = filteredQuestions.reduce((acc, q) => {
    if (!acc[q.category]) {
      acc[q.category] = [];
    }
    acc[q.category].push(q);
    return acc;
  }, {} as Record<string, Question[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p>Loading questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1>Question Library</h1>
        <p className="text-muted-foreground mt-2">
          Manage help text for all discovery questions across categories
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2">
            <Info className="size-5 text-blue-600" />
            <div>
              <p className="text-sm text-muted-foreground">Total Questions</p>
              <p className="text-2xl">{questions.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="size-5 text-green-600" />
            <div>
              <p className="text-sm text-muted-foreground">With Help Text</p>
              <p className="text-2xl">
                {questions.filter((q) => q.helpText).length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="size-5 text-orange-600" />
            <div>
              <p className="text-sm text-muted-foreground">Missing Help Text</p>
              <p className="text-2xl">
                {questions.filter((q) => !q.helpText).length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2">
            <Edit className="size-5 text-purple-600" />
            <div>
              <p className="text-sm text-muted-foreground">Custom Overrides</p>
              <p className="text-2xl">
                {questions.filter((q) => q.hasOverride).length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search questions by text or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-64">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={filterMissingHelpText ? 'default' : 'outline'}
          onClick={() => setFilterMissingHelpText(!filterMissingHelpText)}
          className="gap-2"
        >
          <Filter className="size-4" />
          Missing HelpText Only
        </Button>
      </div>

      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        Showing {filteredQuestions.length} of {questions.length} questions
      </div>

      {/* Questions Table - Grouped by Category */}
      <div className="space-y-8">
        {Object.entries(groupedQuestions).map(([category, categoryQuestions]) => (
          <div key={category} className="space-y-4">
            <h2 className="flex items-center gap-2">
              {CATEGORY_LABELS[category] || category}
              <Badge variant="secondary">{categoryQuestions.length}</Badge>
            </h2>

            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Status</TableHead>
                    <TableHead className="w-32">Question ID</TableHead>
                    <TableHead>Question Text</TableHead>
                    <TableHead className="w-32">Type</TableHead>
                    <TableHead className="w-96">Help Text</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categoryQuestions.map((question) => (
                    <TableRow key={question.id}>
                      <TableCell>
                        {question.helpText ? (
                          <Popover>
                            <PopoverTrigger>
                              <CheckCircle className="size-5 text-green-600 cursor-help" />
                            </PopoverTrigger>
                            <PopoverContent className="w-80">
                              <div className="space-y-2">
                                <p className="text-sm">Has help text</p>
                                {question.hasOverride && (
                                  <p className="text-xs text-muted-foreground">
                                    Custom override applied
                                  </p>
                                )}
                                {question.lastModified && (
                                  <p className="text-xs text-muted-foreground">
                                    Modified: {new Date(question.lastModified).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <AlertCircle className="size-5 text-orange-600" />
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {question.id}
                      </TableCell>
                      <TableCell>{question.questionText}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {QUESTION_TYPE_LABELS[question.questionType] || question.questionType}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {question.helpText ? (
                          <div className="flex items-start gap-2">
                            <Info className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm line-clamp-2">{question.helpText}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">
                            No help text
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(question)}
                          >
                            <Edit className="size-4" />
                          </Button>
                          {question.hasOverride && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRevert(question.id)}
                              title="Revert to default"
                            >
                              <X className="size-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingQuestion} onOpenChange={(open) => !open && setEditingQuestion(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Help Text</DialogTitle>
            <DialogDescription>
              Update the help text that appears in the info icon (ⓘ) next to this question
            </DialogDescription>
          </DialogHeader>

          {editingQuestion && (
            <div className="space-y-4">
              {/* Question Preview */}
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{editingQuestion.category}</Badge>
                  <Badge variant="secondary">
                    {QUESTION_TYPE_LABELS[editingQuestion.questionType]}
                  </Badge>
                </div>
                <p className="font-mono text-xs text-muted-foreground">
                  {editingQuestion.id}
                </p>
                <p>{editingQuestion.questionText}</p>
                {editingQuestion.placeholder && (
                  <p className="text-sm text-muted-foreground italic">
                    Placeholder: {editingQuestion.placeholder}
                  </p>
                )}
                {editingQuestion.options && (
                  <div className="text-sm">
                    <p className="font-medium">Options:</p>
                    <ul className="list-disc list-inside pl-2">
                      {editingQuestion.options.map((opt, idx) => (
                        <li key={idx}>{opt}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* HelpText Editor */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Help Text</label>
                <Textarea
                  value={editedHelpText}
                  onChange={(e) => setEditedHelpText(e.target.value)}
                  placeholder="Explain what we'll DO with this answer and HOW it impacts the implementation..."
                  rows={4}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  Tip: Explain the <strong>impact</strong> of the answer on configuration, not just what the question means.
                </p>
              </div>

              {/* Preview */}
              {editedHelpText && (
                <div className="p-4 border rounded-lg space-y-2">
                  <p className="text-sm font-medium">Preview:</p>
                  <div className="flex items-start gap-2">
                    <Info className="size-5 text-blue-600 mt-0.5" />
                    <p className="text-sm">{editedHelpText}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingQuestion(null)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="size-4 mr-2" />
                  Save HelpText
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}